import { useState, useEffect, useContext } from "react";
// import TwitterEmbed from "./TwitterEmbed";
import { Box, Typography, Button, Card, CardHeader, Avatar, CardContent, Stack, Link, Tooltip } from "@mui/material";
import { format } from "timeago.js";
import { useRouter } from "next/router";
import { LoadingButton } from "@mui/lab";
import ResultBar from "./ResultBar";
import DialogSignIn from "./DialogSignIn";
import AccountContext from "../context/AccountContext";
import downloadCSV from "../helpers/downloadCSV";
import LocalPoliceOutlinedIcon from "@mui/icons-material/LocalPoliceOutlined";
import logEvent from "../helpers/logEvent";

export default function Poll(props) {
  const { poll, showResults, onSubmit, submittedVote, message, pid, detailed } = props;
  const { firebaseUser, accountId } = useContext(AccountContext);
  const [openDialog, setOpenDialog] = useState(false);

  useEffect(() => {
    setOpenDialog(props.showSignInDialog);
  }, [props.showSignInDialog]);

  const router = useRouter();

  const total = poll.choices.reduce((acc, curr, index) => {
    return acc + ((poll.votes && poll.votes[`${index}`]) || 0);
  }, 0);

  // const winner = Object.keys(poll.votes).reduce((a, b) => (poll.votes[a] > poll.votes[b] ? a : b));
  const handleClick = idx => {
    if (poll.signed && !firebaseUser) {
      setOpenDialog(true);
      return;
    }
    onSubmit(idx);
  };

  const choicesMarkUp = poll.choices.map((row, idx) => {
    return (
      <LoadingButton
        key={idx}
        variant="outlined"
        fullWidth
        loading={props.showLoader}
        sx={{ mt: 1, textTransform: "none" }}
        onClick={e => {
          e.preventDefault();
          handleClick(idx);
        }}
      >
        {row}
      </LoadingButton>
    );
  });

  const resultsMarkup = poll.choices.map((row, idx) => {
    let pc = 0;
    if (total > 0) {
      pc = Math.floor((10 * (((poll.votes && poll.votes[`${idx}`]) || 0) * 100)) / total) / 10;
    }
    const showTick = submittedVote === idx;

    return (
      <ResultBar
        key={idx}
        idx={idx}
        text={row}
        pc={pc}
        showTick={showTick}
        hidePublicVoteCount={poll.hidePublicVoteCount}
        data={poll.details?.[idx]}
      />
    );
  });

  const ended = new Date() > poll.endAt.toDate();
  const preText = poll.hidePublicVoteCount ? "" : `${total} votes · `;

  const cardMarkup = (
    <Card>
      <CardHeader
        avatar={<Avatar style={{ backgroundColor: "#ddd" }}>{(poll.createdByName || "Anonymous")[0]}</Avatar>}
        title={poll.createdByName || "Anonymous"}
        subheader={
          <Typography variant="caption" component="h1">
            {poll.createdAt.toDate().toLocaleTimeString("en-us", {
              weekday: "long",
              year: "numeric",
              month: "short",
              day: "numeric",
              hour: "2-digit",
              minute: "2-digit",
            })}
          </Typography>
        }
      />
      <CardContent sx={{ pt: 0 }}>
        <Typography variant="h5" component="h1">
          {poll.question}
        </Typography>
        {showResults ? resultsMarkup : choicesMarkUp}
        <Stack direction="row" alignItems="flex-end" spacing={1} justifyContent="space-between">
          <Typography sx={{ mt: 4 }} variant="body2" color="textSecondary" component="p">
            {preText}
            {ended ? "Final results" : `Ending ${format(poll.endAt.toDate().getTime())}`}
          </Typography>
          {poll.signed && (
            <Tooltip
              enterTouchDelay={10}
              title="This poll requires you to sign in to vote. Your name & email will also be submitted along with your choice."
            >
              <LocalPoliceOutlinedIcon fontSize="small" color="disabled" />
            </Tooltip>
          )}
        </Stack>
        <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
          <Link color="inherit">
            <Typography
              sx={{ cursor: "pointer" }}
              variant="body2"
              color="textSecondary"
              component="p"
              onClick={e => {
                e.preventDefault();
                logEvent("clickedSharePoll");
                router.push("/[pid]/share", `/${pid}/share`);
              }}
            >
              Share Poll
            </Typography>
          </Link>
          {accountId && poll.accountId == accountId && !detailed && (
            <>
              <Typography variant="body2" color="textSecondary" component="p">
                {" "}
                •{" "}
              </Typography>
              <Link color="inherit">
                <Typography
                  sx={{ cursor: "pointer" }}
                  variant="body2"
                  color="textSecondary"
                  component="p"
                  onClick={e => {
                    e.preventDefault();
                    router.push("/r/[accountId]/[pid]", `/r/${poll.accountId}/${pid}`);
                  }}
                >
                  See results
                </Typography>
              </Link>
            </>
          )}
          {accountId && poll.accountId == accountId && detailed && poll.signed && (
            <>
              <Typography variant="body2" color="textSecondary" component="p">
                {" "}
                •{" "}
              </Typography>
              <Link underline="always" color="inherit">
                <Typography
                  sx={{ cursor: "pointer" }}
                  variant="body2"
                  color="textSecondary"
                  component="p"
                  onClick={e => {
                    e.preventDefault();
                    downloadCSV(poll);
                  }}
                >
                  Download results
                </Typography>
              </Link>
            </>
          )}
        </Stack>
      </CardContent>
    </Card>
  );

  const cta = showResults && (
    <Stack alignItems="center">
      <Box>
        <Button
          sx={{ m: 4 }}
          variant="contained"
          color="primary"
          onClick={e => {
            e.preventDefault();
            router.push("/");
          }}
        >
          Create a new poll
        </Button>
      </Box>
    </Stack>
  );

  return (
    <Box>
      <DialogSignIn open={openDialog} handleClose={() => setOpenDialog(false)} message={message} />
      {cardMarkup}
      {cta}
    </Box>
  );
}
